import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';

const formatTokens = (template, tokens) =>
	Object.keys(tokens || {}).reduce(
		(acc, key) => acc.replace(`{${key}}`, tokens[key]),
		template || ''
	);

const IconClose = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M6 6l12 12M18 6 6 18" />
	</svg>
);

const IconCheck = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.6"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m5 12 5 5L20 7" />
	</svg>
);

const IconArrowRight = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14M13 5l7 7-7 7" />
	</svg>
);

// Special cell value markers
const CELL_CHECK = 'check';
const CELL_DASH = 'dash';
const CELL_SOON = 'soon';

const renderCell = ({ value, diff }) => {
	if (value === CELL_DASH) {
		return <span className="plan-compare-cell__dash">—</span>;
	}
	if (value === CELL_CHECK) {
		return (
			<span
				className={
					diff
						? 'plan-compare-cell__check plan-compare-cell__check--diff'
						: 'plan-compare-cell__check'
				}
			>
				<IconCheck />
			</span>
		);
	}
	if (value === CELL_SOON) {
		return (
			<span className="plan-compare-pill plan-compare-pill--soon-blue">
				COMING SOON
			</span>
		);
	}
	return <span className="plan-compare-cell__text">{value}</span>;
};

const PlanCompareModal = ({
	open,
	onClose,
	onRequest,
	onRequestEnterprise,
	isSubmitting,
	canRequest,
	canRequestEnterprise,
	isVerified,
	currentPlan,
	priceTokens
}) => {
	const dictionary = useDictionary();

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const handleKey = (e) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', handleKey);
		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener('keydown', handleKey);
		};
	}, [open, onClose]);

	if (!open) {
		return null;
	}

	const t = (key) => formatTokens(dictionary[key], priceTokens);

	// Enterprise column: rows omit `ent` to inherit the Business value
	// (all-from-business); only deltas are spelled out.
	const sections = [
		{
			title: dictionary.plan_compare_section_pricing,
			rows: [
				{
					feat: dictionary.plan_compare_row_monthly_cost,
					basic: dictionary.plan_compare_value_free,
					biz: t('plan_compare_value_monthly_business'),
					ent: t('plan_compare_value_monthly_enterprise'),
					diff: false
				},
				{
					feat: t('plan_compare_row_semi_annual_cost'),
					basic: dictionary.plan_compare_value_free,
					biz: t('plan_compare_value_semi_annual_business'),
					ent: t('plan_compare_value_semi_annual_enterprise'),
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_annual_cost,
					basic: dictionary.plan_compare_value_free,
					biz: t('plan_compare_value_annual_business'),
					ent: t('plan_compare_value_annual_enterprise'),
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_online_commission,
					basic: t('plan_compare_value_basic_commission'),
					biz: t('plan_compare_value_business_commission'),
					ent: t('plan_compare_value_enterprise_commission'),
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_offline_commission,
					basic: CELL_DASH,
					biz: t('plan_compare_value_offline_commission'),
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_stripe_fees,
					basic: dictionary.plan_compare_value_included,
					biz: dictionary.plan_compare_value_included,
					diff: false
				}
			]
		},
		{
			title: dictionary.plan_compare_section_selling,
			rows: [
				{
					feat: dictionary.plan_compare_row_online_website,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_online_marketplace,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_share_links,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_reseller,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_google_ttd,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_collections,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_group_rates,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_offline_booking,
					basic: CELL_DASH,
					biz: CELL_CHECK,
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_recommended_places,
					basic: CELL_DASH,
					biz: CELL_CHECK,
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_white_label,
					basic: CELL_DASH,
					biz: CELL_DASH,
					ent: CELL_CHECK,
					diff: true
				}
			]
		},
		{
			title: dictionary.plan_compare_section_customer,
			rows: [
				{
					feat: dictionary.plan_compare_row_customer_mgmt,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_reviews,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_qr_checkin,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_participant_qr,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_booking_requests,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_customer_contact,
					basic: dictionary.plan_compare_value_name_only,
					biz: dictionary.plan_compare_value_full_contact,
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_cancellation_policies,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_languages,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_eco_flag,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				}
			]
		},
		{
			title: dictionary.plan_compare_section_operations,
			rows: [
				{
					feat: dictionary.plan_compare_row_calendar,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_bulk_slots,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_resource_calendar,
					basic: CELL_DASH,
					biz: CELL_SOON,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_team_mgmt,
					basic: CELL_DASH,
					biz: CELL_SOON,
					diff: false
				}
			]
		},
		{
			title: dictionary.plan_compare_section_support,
			rows: [
				{
					feat: dictionary.plan_compare_row_email_support,
					basic: CELL_CHECK,
					biz: CELL_CHECK,
					diff: false
				},
				{
					feat: dictionary.plan_compare_row_priority_support,
					basic: CELL_DASH,
					biz: CELL_CHECK,
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_feature_requests,
					basic: CELL_DASH,
					biz: CELL_CHECK,
					diff: true
				},
				{
					feat: dictionary.plan_compare_row_dedicated_support,
					basic: CELL_DASH,
					biz: CELL_DASH,
					ent: CELL_CHECK,
					diff: true
				}
			]
		}
	];

	const entValue = (row) => (row.ent !== undefined ? row.ent : row.biz);

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleCtaClick = () => {
		if (!canRequest || isSubmitting) {
			return;
		}
		onClose();
		onRequest();
	};

	const handleEnterpriseCtaClick = () => {
		if (!canRequestEnterprise || isSubmitting) {
			return;
		}
		onClose();
		onRequestEnterprise();
	};

	// Each CTA renders only while it is an upgrade: free providers see both,
	// Business providers only the Enterprise one, Enterprise providers none.
	const showBusinessCta = currentPlan === 'free';
	const showEnterpriseCta = currentPlan !== 'enterprise';

	const ctaLabel = isSubmitting
		? dictionary.requesting_business_plan
		: dictionary.plan_tab_upgrade_to_business;

	return (
		<div
			className="plan-compare-overlay"
			onClick={handleOverlayClick}
			role="presentation"
		>
			<div
				className="plan-compare-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="plan-compare-title"
			>
				<header className="plan-compare-modal__header">
					<div>
						<h2
							id="plan-compare-title"
							className="plan-compare-modal__title"
						>
							{dictionary.plan_compare_title}
						</h2>
						<p className="plan-compare-modal__subtitle">
							{dictionary.plan_compare_subtitle}
						</p>
					</div>
					<button
						type="button"
						className="plan-compare-modal__close"
						aria-label={dictionary.plan_compare_close}
						onClick={onClose}
					>
						<IconClose />
					</button>
				</header>

				<div className="plan-compare-modal__body">
					<table className="plan-compare-table">
						<thead>
							<tr>
								<th className="plan-compare-table__th plan-compare-table__th--feat">
									&nbsp;
								</th>
								<th className="plan-compare-table__th plan-compare-table__th--basic">
									{dictionary.plan_tab_basic}
								</th>
								<th className="plan-compare-table__th plan-compare-table__th--biz">
									{dictionary.plan_tab_business}
								</th>
								<th className="plan-compare-table__th plan-compare-table__th--ent">
									{dictionary.plan_tab_enterprise}
								</th>
							</tr>
						</thead>
						<tbody>
							{sections.map((section) => (
								<React.Fragment key={section.title}>
									<tr>
										<td
											className="plan-compare-table__section"
											colSpan={4}
										>
											{section.title}
										</td>
									</tr>
									{section.rows.map((row) => {
										const rowClass = row.diff
											? 'plan-compare-row plan-compare-row--diff'
											: 'plan-compare-row';
										return (
											<tr className={rowClass} key={row.feat}>
												<td className="plan-compare-cell plan-compare-cell--feat">
													{row.feat}
												</td>
												<td className="plan-compare-cell plan-compare-cell--basic">
													{renderCell({
														value: row.basic,
														diff: false
													})}
												</td>
												<td className="plan-compare-cell plan-compare-cell--biz">
													{renderCell({
														value: row.biz,
														diff: row.diff
													})}
												</td>
												<td className="plan-compare-cell plan-compare-cell--biz">
													{renderCell({
														value: entValue(row),
														diff: row.diff
													})}
												</td>
											</tr>
										);
									})}
								</React.Fragment>
							))}
						</tbody>
					</table>

					{/* Mobile stacked layout */}
					<div className="plan-compare-stack">
						{sections.map((section) => (
							<div
								className="plan-compare-stack__section"
								key={section.title}
							>
								<div className="plan-compare-stack__title">
									{section.title}
								</div>
								{section.rows.map((row) => {
									const cardClass = row.diff
										? 'plan-compare-stack__card plan-compare-stack__card--diff'
										: 'plan-compare-stack__card';
									return (
										<div className={cardClass} key={row.feat}>
											<div className="plan-compare-stack__feat">
												{row.feat}
											</div>
											<div className="plan-compare-stack__grid">
												<div>
													<div className="plan-compare-stack__eyebrow">
														{dictionary.plan_tab_basic}
													</div>
													<div>
														{renderCell({
															value: row.basic,
															diff: false
														})}
													</div>
												</div>
												<div>
													<div className="plan-compare-stack__eyebrow plan-compare-stack__eyebrow--biz">
														{dictionary.plan_tab_business}
													</div>
													<div>
														{renderCell({
															value: row.biz,
															diff: row.diff
														})}
													</div>
												</div>
												<div>
													<div className="plan-compare-stack__eyebrow plan-compare-stack__eyebrow--ent">
														{dictionary.plan_tab_enterprise}
													</div>
													<div>
														{renderCell({
															value: entValue(row),
															diff: row.diff
														})}
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						))}
					</div>

					<div className="plan-compare-modal__footnote">
						{dictionary.plan_compare_footnote}
					</div>
				</div>

				<footer className="plan-compare-modal__footer">
					<span className="plan-compare-modal__footer-caption">
						{dictionary.plan_compare_footer_caption}
					</span>
					<div className="plan-compare-modal__footer-actions">
						{showBusinessCta ? (
							<button
								type="button"
								className="plan-compare-modal__cta"
								onClick={handleCtaClick}
								disabled={!canRequest || isSubmitting}
								title={
									!isVerified
										? dictionary.plan_tab_verify_to_upgrade
										: undefined
								}
							>
								{ctaLabel}
								<IconArrowRight />
							</button>
						) : null}
						{showEnterpriseCta ? (
							<button
								type="button"
								className="plan-compare-modal__cta plan-compare-modal__cta--ent"
								onClick={handleEnterpriseCtaClick}
								disabled={!canRequestEnterprise || isSubmitting}
								title={
									!isVerified
										? dictionary.plan_tab_verify_to_upgrade
										: undefined
								}
							>
								{dictionary.plan_tab_upgrade_to_enterprise}
								<IconArrowRight />
							</button>
						) : null}
					</div>
				</footer>
			</div>
		</div>
	);
};

PlanCompareModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onRequest: PropTypes.func.isRequired,
	onRequestEnterprise: PropTypes.func,
	isSubmitting: PropTypes.bool,
	canRequest: PropTypes.bool,
	canRequestEnterprise: PropTypes.bool,
	isVerified: PropTypes.bool,
	currentPlan: PropTypes.string,
	priceTokens: PropTypes.object
};

PlanCompareModal.defaultProps = {
	onRequestEnterprise: () => {},
	isSubmitting: false,
	canRequest: false,
	canRequestEnterprise: false,
	isVerified: false,
	currentPlan: 'free',
	priceTokens: {}
};

export default PlanCompareModal;
